import React, { useState, useCallback, useMemo, useContext, useEffect } from 'react';
import { sendProgramMutation } from '@witness/graphql';
import { useMutation } from '@apollo/react-hooks';
import moment from 'moment';

import { ChangesSaved, ChangesSavedIcon, LoadingDot } from '../profile/components';
import { PseudoParticipantCard } from '../../components/trainee-card/components';
import ProgramCalendar from '../../components/program-calendar/ProgramCalendar';
import AddGroupModal from '../../components/add-group-modal.jsx/AddGroupModal';
import ParticipantCard from '../../components/trainee-card/ParticipantCard';
import SelectStatus from '../../components/programs-header/SelectStatus';
import ArrowIcon from '../../assets/common/arrow-main-right.svg';
import CustomButton from '../../components/button/CustomButton';
import { CoachProgramStatus } from '../../constants/enums';
import Checkbox from '../../components/checkbox/Checkbox';
import ChatContext from '../../services/ChatContext';
import { workoutToMin } from '../../services/utils';
import Loader from '../../components/loader/Loader';
import MessageList from '../chat/MessageList';
import IMAGES from '../../constants/images';
import { useProgram } from '../../hooks';

import {
  StatusBar,
  StatusTitle,
  StatusText,
  StatusMessage,
  StatusMessageContainer,
  IconContainer,
  Container,
  Sidemenu,
  Content,
  SideHeader,
  Avatar,
  TraineeName,
  TraineeNameTitle,
  TraineeNameSubTitle,
  SideContent,
  SideInfo,
  InfoHeader,
  InfoHeaderText,
  InfoCell,
  Row,
  Cell,
  CellTitle,
  MinimizeButton,
  Header,
  ContentHeader,
  Tabs,
  Tab,
  ButotnContainer,
  ButtonText,
  GroupIdBadge,
  InfoHeaderEditIcon,
  ParticipantSliderItem,
} from './components';
import AreYouSureModal from '../../components/are-you-sure-modal/AreYouSureModal';
import EmptyTemplate from '../../components/empty-template/EmptyTemplate';
import MagicScroll from '../../components/magic-scroll/MagicScroll';

const Program = () => {
  const [edittingGroup, setEdditingGroup] = useState(null);
  const [sliderIndex, setSliderIndex] = useState(0);
  const [menuIsOpen, setMenuOpen] = useState(true);
  const [sendGroupProgramModal, setSendGroupProgramModal] = useState(false);
  const [tab, setTab] = useState('program');
  const [statusLoading, setStatusLoading] = useState(false);
  const {
    program,
    programLoading,
    isGroup,
    defaultTab,
    urlProgramDay,
    urlTraineeUid,
    refetch,
    handleCoachProgramUpdate,
    updateCalendarCache,
  } = useProgram();

  useEffect(() => {
    setTab(defaultTab || 'program');
  }, [defaultTab]);

  const [sendProgram, { loading }] = useMutation(sendProgramMutation);
  const handleSendProgram = useCallback(async () => {
    setStatusLoading(true);
    await sendProgram({
      variables: {
        record: {
          uid: program?.coachProgram?.uid,
          beginningDate: program?.coachProgram?.beginningDate || new Date(),
        },
      },
    });
    await refetch();
    setStatusLoading(false);
  }, [sendProgram, program, refetch]);

  const diffHours = moment(program?.approvedDate).add(3, 'd').diff(moment(), 'hours');

  const message = useMemo(() => {
    if (!program?.approvedDate) {
      return '';
    }

    if (diffHours < 0) {
      return `You are late by ${-diffHours} hours. Send program to ${program?.trainee?.fullName?.toLowerCase()} ASAP!`;
    }

    return `You have ${diffHours} hours left to send program to customer`;
  }, [diffHours, program]);

  const {
    currentConversation,
    currentMessages,
    conversations,
    setCurrentConversation,
  } = useContext(ChatContext);

  const handleTabClick = useCallback(() => {
    const { uid, trainee } = program;
    const convId = isGroup ? uid : trainee.user?.uid;

    let convo = conversations[convId];

    if (!convo) {
      convo = {
        isNew: true,
        isGroup,
        groupId: convId,
        lastMessage: {},
        user: {
          id: convId,
          name: isGroup ? program.name : trainee.fullName,
          avatar: isGroup ? program.groupImage?.url : trainee.avatar?.url,
        },
      };
    }

    setCurrentConversation(convo);
    setTab('chat');
  }, [isGroup, conversations, program, setCurrentConversation]);

  useEffect(() => {
    if (program && defaultTab === 'chat') {
      handleTabClick();
    }
  }, [defaultTab, handleTabClick, program]);

  return (
    <>
      {program?.coachProgram ? (
        <>
          {!programLoading && (
            <StatusBar>
              {
                <div
                  style={{
                    marginLeft: 28,
                    display: 'flex',
                    justifyContent: isGroup ? 'flex-end' : 'flex-start',
                    alignItems: 'center',
                    flexGrow: isGroup ? '1' : 'initial',
                  }}
                >
                  <StatusTitle>STATUS:</StatusTitle>
                  {isGroup ? (
                    <>
                      <SelectStatus
                        style={{ minWidth: '26rem' }}
                        options={[
                          { value: 'APPROVED', label: 'Building Training Program' },
                          { value: 'ONGOING', label: 'Open For Trainees' },
                        ]}
                        value={
                          program?.coachProgram?.status === CoachProgramStatus.ONGOING ||
                          statusLoading
                            ? { value: 'ONGOING', label: 'Open For Trainees' }
                            : program?.coachProgram?.status === CoachProgramStatus.APPROVED
                            ? { value: 'APPROVED', label: 'Building Training Program' }
                            : { value: '', label: 'Loading...' }
                        }
                        onChange={(v) => {
                          if (v?.value === 'ONGOING') {
                            setSendGroupProgramModal(true);
                          }
                        }}
                      />
                      <AreYouSureModal
                        modalOpen={sendGroupProgramModal}
                        closeModal={() => setSendGroupProgramModal(false)}
                        submitModal={handleSendProgram}
                        headingText={'Are You Sure?'}
                        contentText={
                          <>
                            If you will change the status to “<span>Open</span>” the group program
                            will be available for trainees
                          </>
                        }
                        choiceQuestion={'Do you want to Change the Status?'}
                      />
                    </>
                  ) : (
                    <StatusText>
                      {program?.coachProgram?.status === CoachProgramStatus.ONGOING
                        ? 'In Progress '
                        : 'Building '}
                      Training Program
                    </StatusText>
                  )}
                </div>
              }

              {!isGroup && program?.coachProgram?.status !== CoachProgramStatus.ONGOING && (
                <StatusMessageContainer>
                  <IconContainer isDue={diffHours < 0}>
                    <img src={ArrowIcon} alt="" />
                  </IconContainer>
                  <StatusMessage>{message}</StatusMessage>
                </StatusMessageContainer>
              )}
            </StatusBar>
          )}
          <Container>
            <Header>
              <SideHeader>
                <Avatar src={isGroup ? program?.groupImage?.url : program?.trainee?.avatar?.url}>
                  {program?.trainee?.fullName
                    ?.split(' ')
                    .map((x) => x.substring(0, 1))
                    .join('')}
                </Avatar>
                {isGroup ? (
                  <TraineeName>
                    <TraineeNameTitle>{program?.name}</TraineeNameTitle>
                    <GroupIdBadge>
                      #
                      {(program?.code?.digits &&
                        `${('000' + Math.floor(program?.code?.digits / 1000)).slice(-3)}-${(
                          '000' +
                          (program?.code?.digits % 1000)
                        ).slice(-3)}`) ||
                        '--- ---'}
                    </GroupIdBadge>
                  </TraineeName>
                ) : (
                  <TraineeName>
                    <TraineeNameTitle>
                      {(program?.trainee?.fullName && `${program?.trainee?.fullName}'s`) || false}
                    </TraineeNameTitle>
                    <TraineeNameSubTitle>Program</TraineeNameSubTitle>
                  </TraineeName>
                )}
              </SideHeader>

              <ContentHeader>
                <Tabs>
                  <Tab
                    text="Program Planning"
                    active={tab === 'program'}
                    onClick={() => setTab('program')}
                  />
                  <Tab text="Chat" active={tab === 'chat'} onClick={handleTabClick} />
                  {isGroup && (
                    <Tab
                      text="Participants"
                      active={tab === 'participants'}
                      onClick={() => setTab('participants')}
                    />
                  )}
                </Tabs>
                {!isGroup && (
                  <ButotnContainer>
                    {program?.coachProgram?.status === CoachProgramStatus.APPROVED && (
                      <CustomButton
                        backgroundColor="#00FF91"
                        height="48px"
                        fontSize="1.4rem"
                        fontFamily="Roboto-r"
                        onClick={handleSendProgram}
                        disabled={loading}
                      >
                        <ButtonText>Send Program</ButtonText>
                      </CustomButton>
                    )}

                    {loading && (
                      <>
                        <LoadingDot i={2} />
                        <LoadingDot i={1} />
                        <LoadingDot i={0} />
                      </>
                    )}

                    {program?.coachProgram?.status === CoachProgramStatus.ONGOING && (
                      <ChangesSaved>
                        <ChangesSavedIcon />
                        In progress
                      </ChangesSaved>
                    )}
                  </ButotnContainer>
                )}
              </ContentHeader>
            </Header>
            <Content>
              <Sidemenu isOpen={menuIsOpen}>
                <SideContent isOpen={menuIsOpen}>
                  <MinimizeButton onClick={() => setMenuOpen((o) => !o)} isOpen={menuIsOpen} />

                  <SideInfo className="side-info">
                    {isGroup && (
                      <Row style={{ position: 'relative' }}>
                        <InfoHeader
                          style={{
                            border: 'none',
                            width: 'fit-content',
                            padding: '0 1rem',
                            left: 'Calc(50% - 5.3rem)',
                          }}
                        >
                          <AddGroupModal
                            modalOpen={!!edittingGroup}
                            initialValues={edittingGroup}
                            submitModal={() => {}}
                            closeModal={() => setEdditingGroup(false)}
                          />
                          <InfoHeaderText>Edit Group</InfoHeaderText>
                          <InfoHeaderEditIcon onClick={() => setEdditingGroup(program)} />
                        </InfoHeader>

                        <Cell mb style={{ padding: '2.4rem 1.2rem 2rem' }}>
                          <Row
                            style={{
                              justifyContent: 'center',
                              width: 'Calc(100% - 1.2rem)',
                              margin: '1.6rem 0 2.4rem',
                            }}
                          >
                            {isGroup && program?.coachProgram?.participants?.length && (
                              <MagicScroll
                                noArrows={program?.coachProgram?.participants?.length <= 5}
                                itemsToDisplay={5}
                                items={program?.coachProgram?.participants}
                                ItemComponent={ParticipantSliderItem}
                                onLeftClick={() => setSliderIndex((curr) => Math.max(0, curr - 1))}
                                onRightClick={() => setSliderIndex((curr) => Math.min(5, curr + 1))}
                                firstItem={sliderIndex}
                                keyExtractor={(a, b) => (typeof b === 'number' ? b : a)}
                                onTabClick={() => {}}
                              />
                            )}
                          </Row>
                        </Cell>
                      </Row>
                    )}
                    <Row style={{ position: 'relative' }}>
                      <InfoHeader>
                        <InfoHeaderText>Program Details</InfoHeaderText>
                      </InfoHeader>
                      <InfoCell
                        mr
                        mb
                        title="Program Length"
                        text={`${program?.programLength} days`}
                      />
                      <InfoCell
                        mb
                        title="Training Types"
                        text={
                          program?.trainingTypes?.map((a) => a.name)?.join(', ') ||
                          program?.trainingType?.name
                        }
                      />
                    </Row>
                    {isGroup ? (
                      <Row>
                        <InfoCell
                          mb
                          mr
                          title="Maximum number of participants"
                          text={`${program?.participants}`}
                        />
                        <InfoCell
                          mb
                          title="Price per participant per month"
                          text={`₪${program?.price}`}
                        />
                      </Row>
                    ) : (
                      <Row>
                        <InfoCell
                          mb
                          mr
                          title="Workout time"
                          text={`${workoutToMin(program?.workoutLength)} mi`}
                        />
                        <InfoCell
                          mb
                          title="Trainer Goals"
                          text={program?.goals?.map((eq) => eq.name).join(', ')}
                        />
                      </Row>
                    )}

                    <Row>
                      <Cell mb style={{ padding: '2.4rem 1.2rem 2rem' }}>
                        <CellTitle>Desired Agenda</CellTitle>

                        <Row
                          style={{
                            justifyContent: 'space-between',
                            width: 'Calc(100% - 1.2rem)',
                            margin: '1.6rem 0 2.4rem',
                          }}
                        >
                          <Checkbox
                            text="S"
                            checked={program?.daysOfTraining.indexOf('SUN') > -1}
                          />
                          <Checkbox
                            text="M"
                            checked={program?.daysOfTraining.indexOf('MON') > -1}
                          />
                          <Checkbox
                            text="T"
                            checked={program?.daysOfTraining.indexOf('TUE') > -1}
                          />
                          <Checkbox
                            text="W"
                            checked={program?.daysOfTraining.indexOf('WED') > -1}
                          />
                          <Checkbox
                            text="T"
                            checked={program?.daysOfTraining.indexOf('THUR') > -1}
                          />
                          <Checkbox
                            text="F"
                            checked={program?.daysOfTraining.indexOf('FRI') > -1}
                          />
                          <Checkbox
                            text="S"
                            checked={program?.daysOfTraining.indexOf('SAT') > -1}
                          />
                        </Row>
                      </Cell>
                    </Row>

                    <Row style={{ position: 'relative' }}>
                      <InfoHeader>
                        <InfoHeaderText>
                          {isGroup ? `Group Description` : `Program Details`}
                        </InfoHeaderText>
                      </InfoHeader>
                      {!isGroup && (
                        <>
                          <InfoCell mr mb title="Fitness Level" text={program?.fitnessLevel} />
                          <InfoCell
                            mb
                            title="Training Environment"
                            text={program?.place?.join(', ')}
                          />
                        </>
                      )}
                    </Row>

                    {isGroup ? (
                      <Row>
                        <InfoCell mr mb title="" text={program?.description} />
                      </Row>
                    ) : (
                      <>
                        <Row>
                          <InfoCell
                            mr
                            mb
                            title="Available Equipment"
                            text={
                              program?.equipments?.map((eq) => eq.name).join(', ') ||
                              "Trainee didn't mention any available equipment"
                            }
                          />
                        </Row>

                        <Row>
                          <InfoCell
                            mr
                            title="Health Status"
                            text={
                              (program?.healthCondition && program?.healthConditionDetails) ||
                              'Healthy'
                            }
                          />
                          <InfoCell
                            title="Injuries"
                            text={`${program?.injury || ''}${
                              (program?.injuryDetails && '. ' + program?.injuryDetails) || ''
                            }`}
                          />
                        </Row>
                      </>
                    )}
                  </SideInfo>
                </SideContent>
              </Sidemenu>
              {tab === 'program' && (
                <ProgramCalendar
                  isOngoing={program?.coachProgram?.status === CoachProgramStatus.ONGOING}
                  programLength={program?.programLength}
                  handleCoachProgramUpdate={handleCoachProgramUpdate}
                  calendar={program?.coachProgram?.calendar}
                  coachProgramUid={program?.coachProgram?.uid}
                  beginningDate={program?.coachProgram?.beginningDate}
                  updateCalendarCache={updateCalendarCache}
                  refetch={refetch}
                  loading={loading}
                  urlProgramDay={urlProgramDay}
                  urlTraineeUid={urlTraineeUid}
                />
              )}
              {isGroup && tab === 'participants' && (
                <div
                  style={{
                    padding: '2rem',
                    display: 'flex',
                    justifyContent: 'space-evenly',
                    flexWrap: 'wrap',
                    height: 'fit-content',
                    width: '100%',
                  }}
                >
                  {program?.coachProgram?.participants?.length ? (
                    <>
                      {program?.coachProgram?.participants?.map((participant) => (
                        <ParticipantCard participant={participant} />
                      ))}
                      <PseudoParticipantCard />
                      <PseudoParticipantCard />
                      <PseudoParticipantCard />
                      <PseudoParticipantCard />
                      <PseudoParticipantCard />
                    </>
                  ) : (
                    <EmptyTemplate
                      image={IMAGES.EmptyTrainees}
                      primaryText={`No Participants Yet`}
                    />
                  )}
                </div>
              )}
              {tab === 'chat' && (
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    flexShrink: 0,
                  }}
                >
                  <MessageList
                    conversation={currentConversation}
                    messages={currentMessages}
                    hideHeader
                  />
                </div>
              )}
            </Content>
          </Container>
        </>
      ) : !program && !programLoading ? (
        <>program not found</>
      ) : program && !program?.coachProgram ? (
        <>coach program is not created yet, you may need to reload the page</>
      ) : (
        <Loader />
      )}
    </>
  );
};

export default Program;
///home?request_id=714216ec-9646-4101-8c6b-ac64b9f0c350
